/**
 * HiBFF Schools Programme — Admin Command Centre.
 *
 * Three tabs (Phases F + G + I):
 *   • Overview        — every school cohort, KPIs, drill-down trigger
 *   • Sankey          — flow analytics across the 20-challenge journey
 *   • Content Manager — CRUD on the 70 challenge variants
 *
 * Design promise: NEVER displays an individual teen's identifier or
 * conversation. Every figure is an aggregate. Per spec §6.
 *
 * Sankey is a custom SVG (no @nivo dependency) so we don't churn the
 * package.json this close to production.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, GraduationCap, GitBranch, BookOpenCheck, Loader2, Building2 } from 'lucide-react';
import { apiClient } from '../../api/client';
import OutreachTracker from './OutreachTracker';
import AdminLayout from '../../components/layout/AdminLayout';

const BRAND_ORANGE = '#F5831F';
const BRAND_PURPLE = '#7B4FD8';

const Tab = ({ active, onClick, icon: Icon, children, testId }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
      active ? 'bg-white text-gray-900' : 'bg-white/5 text-white/70 hover:bg-white/10'
    }`}
    data-testid={testId}
  >
    <Icon size={16} /> {children}
  </button>
);

// ── Overview ───────────────────────────────────────────────────────────────

const Overview = () => {
  const [data, setData] = useState(null);
  const [drilldown, setDrilldown] = useState(null);

  useEffect(() => {
    apiClient.request('/api/admin/schools/overview').then(setData).catch(() => setData({ schools: [] }));
  }, []);

  const openDrill = async (sid) => {
    try {
      const d = await apiClient.request(`/api/admin/schools/${sid}`);
      setDrilldown(d);
    } catch (_e) { /* ignore */ }
  };

  if (!data) return <div className="text-white/60"><Loader2 className="animate-spin" /></div>;

  return (
    <div data-testid="schools-overview">
      <h2 className="text-white text-lg font-semibold mb-3">All school cohorts</h2>
      {data.schools.length === 0 ? (
        <p className="text-white/60 text-sm">No active schools yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-sm text-white/90">
            <thead className="bg-white/5 text-white/60 text-left">
              <tr>
                <th className="px-3 py-2">School</th>
                <th className="px-3 py-2 text-right">Active teens</th>
                <th className="px-3 py-2 text-right">Journeys</th>
                <th className="px-3 py-2 text-right">Completed</th>
                <th className="px-3 py-2 text-right">Avg / teen</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.schools.map((s) => (
                <tr key={s.school_id} className="border-t border-white/10">
                  <td className="px-3 py-2">{s.school_name}</td>
                  <td className="px-3 py-2 text-right">{s.active_teens}</td>
                  <td className="px-3 py-2 text-right">{s.teens_with_journey}</td>
                  <td className="px-3 py-2 text-right">{s.challenges_completed}</td>
                  <td className="px-3 py-2 text-right">{s.avg_completed_per_teen}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => openDrill(s.school_id)}
                      className="text-xs text-orange-400 hover:underline"
                      data-testid={`schools-drill-${s.school_id}`}
                    >
                      Drill down →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {drilldown && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4"
          onClick={() => setDrilldown(null)}
          data-testid="schools-drill-modal"
        >
          <div
            className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-lg text-white"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-1">{drilldown.school_name}</h3>
            <p className="text-white/60 text-xs mb-4">No individual teen data is ever shown.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="bg-white/5 rounded-xl p-3">
                <p className="text-white/60 text-xs">Active teens</p>
                <p className="text-2xl font-semibold">{drilldown.active_teens}</p>
              </div>
              <div className="bg-white/5 rounded-xl p-3">
                <p className="text-white/60 text-xs">With journey</p>
                <p className="text-2xl font-semibold">{drilldown.teens_with_journey}</p>
              </div>
              <div className="bg-white/5 rounded-xl p-3">
                <p className="text-white/60 text-xs">Completed</p>
                <p className="text-2xl font-semibold">{drilldown.challenges_completed}</p>
              </div>
              <div className="bg-white/5 rounded-xl p-3">
                <p className="text-white/60 text-xs">Avg / teen</p>
                <p className="text-2xl font-semibold">{drilldown.avg_completed_per_teen}</p>
              </div>
            </div>
            <h4 className="mt-4 mb-2 text-sm font-medium">Top themes completed</h4>
            <ul className="text-xs text-white/80 space-y-1">
              {drilldown.by_theme.map((t) => (
                <li key={t.theme} className="flex justify-between">
                  <span>{t.theme.replace(/_/g, ' ')}</span>
                  <span className="text-white/60">{t.count}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Sankey ─────────────────────────────────────────────────────────────────

const Sankey = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    apiClient.request('/api/admin/schools/sankey').then(setData).catch(() => setData({ nodes: [], links: [] }));
  }, []);

  const layout = useMemo(() => {
    if (!data?.nodes?.length) return null;
    // Group nodes by step (numeric prefix)
    const steps = {};
    data.nodes.forEach((n) => {
      const m = n.id.match(/^(\d+)\./);
      const step = m ? Number(m[1]) : 0;
      steps[step] = steps[step] || [];
      steps[step].push(n.id);
    });
    const stepKeys = Object.keys(steps).map(Number).sort((a, b) => a - b);
    const W = 900;
    const H = 28 * Math.max(...stepKeys.map((k) => steps[k].length), 1) + 80;
    const colW = W / stepKeys.length;
    const positions = {};
    stepKeys.forEach((k, ki) => {
      const col = steps[k];
      col.forEach((id, ri) => {
        positions[id] = {
          x: ki * colW + 30,
          y: 40 + ri * 28,
        };
      });
    });
    return { positions, stepKeys, W, H, links: data.links };
  }, [data]);

  if (!data) return <div className="text-white/60"><Loader2 className="animate-spin" /></div>;
  if (!data.nodes.length) return <p className="text-white/60 text-sm">No completion events yet — Sankey populates once teens complete challenges.</p>;
  if (!layout) return null;

  return (
    <div data-testid="schools-sankey">
      <h2 className="text-white text-lg font-semibold mb-3">Journey flow (Sankey)</h2>
      <div className="overflow-x-auto bg-white/5 border border-white/10 rounded-xl p-4">
        <svg width={layout.W} height={layout.H} className="text-white">
          {layout.links.map((link, i) => {
            const s = layout.positions[link.source];
            const t = layout.positions[link.target];
            if (!s || !t) return null;
            const path = `M ${s.x + 90} ${s.y + 10} C ${(s.x + t.x) / 2} ${s.y + 10}, ${(s.x + t.x) / 2} ${t.y + 10}, ${t.x} ${t.y + 10}`;
            return (
              <path
                key={i}
                d={path}
                stroke={i % 2 === 0 ? BRAND_ORANGE : BRAND_PURPLE}
                strokeOpacity={0.4}
                strokeWidth={Math.min(8, 2 + link.value)}
                fill="none"
              />
            );
          })}
          {Object.entries(layout.positions).map(([id, p]) => (
            <g key={id}>
              <rect x={p.x} y={p.y} width={90} height={20} rx={4} fill={BRAND_PURPLE} fillOpacity={0.7} />
              <text x={p.x + 6} y={p.y + 14} fontSize={10} fill="white">
                {id.replace(/^\d+\.\s*/, '').slice(0, 14)}
              </text>
            </g>
          ))}
        </svg>
      </div>
      <p className="text-white/50 text-xs mt-3">
        Brand colours — orange ({BRAND_ORANGE}) and purple ({BRAND_PURPLE}). Aggregated, no individual teen data.
      </p>
    </div>
  );
};

// ── Content Manager ────────────────────────────────────────────────────────

const ContentManager = () => {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ full_text: '', ava_intro: '', content_status: 'draft' });
  const [saving, setSaving] = useState(false);

  const load = () =>
    apiClient.request('/api/admin/schools/content/library').then((d) => setRows(d.variants));

  useEffect(() => { load(); }, []);

  const startEdit = (row) => {
    setEditing(row);
    setForm({
      full_text: row.full_text || '',
      ava_intro: row.ava_intro || '',
      content_status: row.content_status || 'draft',
    });
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await apiClient.request(`/api/admin/schools/content/library/${editing.challenge_id}`, {
        method: 'PATCH',
        body: JSON.stringify(form),
      });
      await load();
      setEditing(null);
    } finally {
      setSaving(false);
    }
  };

  if (!rows) return <div className="text-white/60"><Loader2 className="animate-spin" /></div>;

  return (
    <div data-testid="schools-content-manager">
      <h2 className="text-white text-lg font-semibold mb-1">Challenge library</h2>
      <p className="text-white/60 text-xs mb-3">
        {rows.length} variants — promote drafts to <strong>live</strong> when ready.
      </p>
      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-sm text-white/90">
          <thead className="bg-white/5 text-white/60 text-left">
            <tr>
              <th className="px-3 py-2">Tier</th>
              <th className="px-3 py-2">Theme</th>
              <th className="px-3 py-2">Variant</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.challenge_id} className="border-t border-white/10">
                <td className="px-3 py-2">T{r.tier}</td>
                <td className="px-3 py-2">{r.theme_label}</td>
                <td className="px-3 py-2">{r.variant}</td>
                <td className="px-3 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    r.content_status === 'live' ? 'bg-emerald-500/20 text-emerald-300' :
                    r.content_status === 'review' ? 'bg-amber-500/20 text-amber-300' :
                    'bg-white/10 text-white/60'
                  }`}>
                    {r.content_status}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => startEdit(r)}
                    className="text-orange-400 text-xs hover:underline"
                    data-testid={`content-edit-${r.challenge_id}`}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4"
          onClick={() => setEditing(null)}
          data-testid="content-edit-modal"
        >
          <div
            className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-2xl text-white"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-semibold mb-1">{editing.theme_label} — Variant {editing.variant}</h3>
            <p className="text-white/60 text-xs mb-3">Tier {editing.tier} · {editing.core_action}</p>

            <label className="text-xs text-white/70 mb-1 block">AvA intro</label>
            <textarea
              rows={3}
              className="w-full mb-3 px-3 py-2 rounded-xl bg-black/30 border border-white/10 text-sm"
              value={form.ava_intro}
              onChange={(e) => setForm({ ...form, ava_intro: e.target.value })}
              data-testid="content-edit-ava-intro"
            />

            <label className="text-xs text-white/70 mb-1 block">Full challenge text</label>
            <textarea
              rows={6}
              className="w-full mb-3 px-3 py-2 rounded-xl bg-black/30 border border-white/10 text-sm"
              value={form.full_text}
              onChange={(e) => setForm({ ...form, full_text: e.target.value })}
              data-testid="content-edit-full-text"
            />

            <label className="text-xs text-white/70 mb-1 block">Status</label>
            <select
              value={form.content_status}
              onChange={(e) => setForm({ ...form, content_status: e.target.value })}
              className="w-full mb-4 px-3 py-2 rounded-xl bg-black/30 border border-white/10 text-sm"
              data-testid="content-edit-status"
            >
              <option value="draft">Draft</option>
              <option value="review">Review</option>
              <option value="live">Live</option>
            </select>

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2 rounded-lg bg-white/10 text-sm"
                data-testid="content-edit-cancel"
              >Cancel</button>
              <button
                onClick={save}
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-orange-500 text-white text-sm disabled:opacity-50"
                data-testid="content-edit-save"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Page wrapper ───────────────────────────────────────────────────────────

const SchoolsCommandCentre = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  return (
    <AdminLayout pageTitle="Schools Programme — Command Centre">
      <div className="rounded-2xl bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-5" data-testid="schools-command-centre">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/vault')}
              className="text-white/60 hover:text-white"
              data-testid="schools-back-button"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-xl font-bold text-white">Schools Programme — Command Centre</h1>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          <Tab active={tab === 'overview'} onClick={() => setTab('overview')} icon={GraduationCap} testId="schools-tab-overview">Overview</Tab>
          <Tab active={tab === 'sankey'} onClick={() => setTab('sankey')} icon={GitBranch} testId="schools-tab-sankey">Sankey</Tab>
          <Tab active={tab === 'content'} onClick={() => setTab('content')} icon={BookOpenCheck} testId="schools-tab-content">Content Manager</Tab>
          <Tab active={tab === 'outreach'} onClick={() => setTab('outreach')} icon={Building2} testId="schools-tab-outreach">Outreach Tracker</Tab>
        </div>

        {tab === 'overview' && <Overview />}
        {tab === 'sankey' && <Sankey />}
        {tab === 'content' && <ContentManager />}
        {tab === 'outreach' && <OutreachTracker />}
      </div>
    </AdminLayout>
  );
};

export default SchoolsCommandCentre;
